import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { isV2 } from "../lib/config";
import type { CreditPackage, Features, FeatureSettings, LedgerEntry, OverrideRequirements, WaiverStatus, WaiverTemplate, WalletView } from "../lib/typesV2";

export const keysV2 = {
  features: ["v2", "features"] as const,
  featureSettings: ["v2", "feature-settings"] as const,
  override: ["v2", "override-requirements"] as const,
  wallet: (guardianId: string) => ["v2", "wallet", guardianId] as const,
  ledger: (walletId: string) => ["v2", "ledger", walletId] as const,
  packages: ["v2", "packages"] as const,
  templates: (locale: string) => ["v2", "waiver-templates", locale] as const,
  waiver: (kidId: string, productId?: string) => ["v2", "waiver-status", kidId, productId ?? ""] as const,
};

/** The effective switches of the current branch: the POS uses them to decide which fields, steps and tabs to show. */
export const useFeatures = () =>
  useQuery({ queryKey: keysV2.features, enabled: isV2(), staleTime: 60_000, queryFn: async () => (await api.get<{ features: Features }>("/features")).features });

export const useFeatureSettings = (enabled: boolean) => useQuery({ queryKey: keysV2.featureSettings, enabled, queryFn: () => api.get<FeatureSettings>("/features/settings") });

export const useOverrideRequirements = (enabled: boolean) => useQuery({ queryKey: keysV2.override, enabled, queryFn: () => api.get<OverrideRequirements>("/overrides/requirements") });

/** `null` = the family has no wallet yet (404). */
export const useWallet = (guardianId: string | null) =>
  useQuery({
    queryKey: keysV2.wallet(guardianId ?? ""), enabled: !!guardianId,
    queryFn: async (): Promise<WalletView | null> => {
      try { return await api.get<WalletView>(`/wallets/by-guardian/${guardianId}`); } catch (e) { if ((e as { status?: number }).status === 404) return null; throw e; }
    },
  });

export const useLedger = (walletId: string | null) =>
  useQuery({ queryKey: keysV2.ledger(walletId ?? ""), enabled: !!walletId, queryFn: async () => (await api.get<{ entries: LedgerEntry[] }>(`/wallets/${walletId}/ledger?limit=10`)).entries });

export const useCreditPackages = (enabled: boolean) => useQuery({ queryKey: keysV2.packages, enabled, queryFn: async () => (await api.get<{ packages: CreditPackage[] }>("/wallets/packages")).packages });

export const useWaiverTemplates = (locale: string, enabled: boolean) =>
  useQuery({ queryKey: keysV2.templates(locale), enabled, queryFn: async () => (await api.get<{ templates: WaiverTemplate[] }>(`/waivers/templates?locale=${locale}`)).templates });

export const useWaiverStatus = (kidId: string | null, productId?: string) =>
  useQuery({ queryKey: keysV2.waiver(kidId ?? "", productId), enabled: !!kidId && isV2(), queryFn: () => api.get<WaiverStatus>(`/waivers/status?kidId=${kidId}${productId ? `&productId=${productId}` : ""}`) });
